import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TILE } from '../../shared/geo.js';
import { Instances } from './instances.js';
import { VEHICLES, paintFor } from './vehicles.js';
const ZERO_MATRIX = new THREE.Matrix4().makeScale(0, 0, 0);

const MAX = 26; // moving vehicles around the player when the town is busy
const NEAR = 70; // metres: nothing appears closer than this
const FAR = 340; // nothing appears farther than this
const GONE = 430; // and beyond this a vehicle is taken away
const NODE = 2; // metres, the cell of the junction lookup
const JOIN = 1.5; // metres within which two road points count as the same junction
// cruising speed in metres per second by road class (0 trunk ... 5 service road)
const SPEED = [15, 14, 12, 11, 8, 5];
const RIDER_SHIRTS = ['#c8402f', '#2f6fb4', '#2f8a56', '#f4f1e8', '#7a3fa0', '#e07a28'];

function colored(geometry, hex) {
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  const c = new THREE.Color(hex);
  const n = g.attributes.position.count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}
const box = (w, h, d, hex, x, y, z) => colored(new THREE.BoxGeometry(w, h, d).translate(x, y, z), hex);

// A rider sitting on a bike, leaning to the handlebars. The body is one shape for every rider;
// the shirt and arms are another, white, so each rider's shirt can be its own colour.
function riderBody() {
  return mergeGeometries([
    box(0.25, 0.26, 0.25, '#5a3a26', 0, 1.7, -0.08),
    box(0.3, 0.14, 0.3, '#22262e', 0, 1.86, -0.08), // helmet
    box(0.15, 0.2, 0.55, '#22262e', 0.17, 0.92, 0.12),
    box(0.15, 0.2, 0.55, '#22262e', -0.17, 0.92, 0.12),
    box(0.14, 0.5, 0.16, '#22262e', 0.17, 0.62, 0.3),
    box(0.14, 0.5, 0.16, '#22262e', -0.17, 0.62, 0.3),
  ]);
}
function riderShirt() {
  return mergeGeometries([box(0.4, 0.55, 0.26, '#ffffff', 0, 1.28, -0.12), box(0.1, 0.12, 0.6, '#ffffff', 0.24, 1.32, 0.26), box(0.1, 0.12, 0.6, '#ffffff', -0.24, 1.32, 0.26)]);
}

// Taxis and bikes going about the town on the real roads. They keep to the
// right, slow for whatever is in front of them, turn at junctions, and are as
// many as the hour and the place call for.
export class Traffic {
  // vehicles: the game's Vehicles, used to set a vehicle on the sloping ground
  constructor(scene, map, vehicles) {
    this.scene = scene;
    this.map = map;
    this.vehicles = vehicles;
    this.roads = new Map(); // tile key -> roads of that tile
    this.nodes = new Map(); // cell -> [{ road, k }] every road point, to find junctions
    this.cars = [];
    this.busy = 0; // 0 empty streets ... 1 the full daytime bustle
    this.bikes = true; // commercial bikes may not run at night
    this.slow = []; // [{ x, z }] control posts: everything crawls past them
    this.clock = 0;
    this.material = new THREE.MeshLambertMaterial({ vertexColors: true });
    // bikes and taxis share the parked vehicles' instance pools; riders have pools of their own
    this.riders = new Instances(scene, riderBody(), this.material, 300);
    this.shirts = new Instances(scene, riderShirt(), this.material, 300, true);
    this.people = null; // set by the game: the people's card atlas dresses the riders
    this.riderCards = null; // seated cards at the riders' slots, once the atlas is baked
    this.shirtColours = RIDER_SHIRTS.map((hex) => new THREE.Color(hex));
    this.seed = 1;
    this.tmp = new THREE.Object3D();
  }

  rand() {
    this.seed = (this.seed + 0x6d2b79f5) >>> 0;
    let t = this.seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  cell(x, z) {
    return Math.floor(x / NODE) * 100003 + Math.floor(z / NODE);
  }

  // Call when a tile is built.
  addTile(t) {
    if (!t.data) return;
    const ox = t.tx * TILE;
    const oz = t.tz * TILE;
    const list = [];
    for (const r of t.data.r) {
      if (r.c > 5 || r.p.length < 4) continue;
      const n = r.p.length / 2;
      const pts = new Float32Array(n * 2);
      for (let k = 0; k < n; k++) {
        pts[k * 2] = ox + r.p[k * 2] / 10;
        pts[k * 2 + 1] = oz + r.p[k * 2 + 1] / 10;
      }
      // oneWay: traffic runs only in the order of the points (each side of a dual carriageway)
      const road = { pts, n, w: r.w, c: r.c, earth: r.s === 1, oneWay: r.o === 1, tile: t.key, live: true };
      list.push(road);
      for (let k = 0; k < n; k++) {
        const key = this.cell(pts[k * 2], pts[k * 2 + 1]);
        let at = this.nodes.get(key);
        if (!at) this.nodes.set(key, (at = []));
        at.push({ road, k });
      }
    }
    this.roads.set(t.key, list);
  }

  removeTile(t) {
    const list = this.roads.get(t.key);
    if (!list) return;
    for (const road of list) {
      road.live = false;
      for (let k = 0; k < road.n; k++) {
        const key = this.cell(road.pts[k * 2], road.pts[k * 2 + 1]);
        const at = this.nodes.get(key);
        if (!at) continue;
        const kept = at.filter((e) => e.road !== road);
        if (kept.length) this.nodes.set(key, kept);
        else this.nodes.delete(key);
      }
    }
    this.roads.delete(t.key);
  }

  // The ways on from point k of a road, apart from going back the way it came:
  // each is { road, k, dir }.
  exits(road, k, cameDir) {
    const out = [];
    if (k + cameDir >= 0 && k + cameDir < road.n) out.push({ road, k, dir: cameDir });
    const x = road.pts[k * 2];
    const z = road.pts[k * 2 + 1];
    const cx = Math.floor(x / NODE);
    const cz = Math.floor(z / NODE);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const at = this.nodes.get((cx + dx) * 100003 + cz + dz);
        if (!at) continue;
        for (const e of at) {
          if (e.road === road) continue;
          if (Math.abs(e.road.pts[e.k * 2] - x) > JOIN || Math.abs(e.road.pts[e.k * 2 + 1] - z) > JOIN) continue;
          if (e.k + 1 < e.road.n) out.push({ road: e.road, k: e.k, dir: 1 });
          if (e.k > 0 && !e.road.oneWay) out.push({ road: e.road, k: e.k, dir: -1 });
        }
      }
    }
    return out;
  }

  // hour 0-24. quiet: a stay-at-home day. people: how many live in the
  // settlement around. bikeBan: whether bikes are kept off the road at night here.
  setTime(hour, quiet, people, bikeBan) {
    let busy = hour < 5 ? 0.04 : hour < 6.5 ? 0.3 : hour < 19 ? 1 : hour < 21.5 ? 0.5 : 0.12;
    if (quiet) busy *= 0.08;
    this.busy = busy * Math.max(0.15, Math.min(1, people / 3000));
    this.bikes = !bikeBan || (hour >= 6.5 && hour < 18.5);
  }

  spawn(px, pz) {
    const tiles = [...this.roads.values()];
    if (tiles.length === 0) return;
    for (let tries = 0; tries < 24; tries++) {
      const list = tiles[Math.floor(this.rand() * tiles.length)];
      if (list.length === 0) continue;
      const road = list[Math.floor(this.rand() * list.length)];
      // main roads carry most of the traffic
      if (this.rand() > [1, 1, 0.8, 0.6, 0.22, 0.06][road.c]) continue;
      const k = Math.floor(this.rand() * (road.oneWay ? road.n - 1 : road.n));
      const x = road.pts[k * 2];
      const z = road.pts[k * 2 + 1];
      const d = Math.hypot(x - px, z - pz);
      if (d < NEAR || d > FAR) continue;
      let dir = road.oneWay || this.rand() < 0.5 ? 1 : -1;
      if (k + dir < 0 || k + dir >= road.n) dir = -dir;
      // okadas lead, then the yellow taxis, then private cars, clando minibuses and pickups
      const roll = this.rand();
      const type = this.bikes && roll < 0.45 ? 'bike' : roll < 0.72 ? 'taxi' : roll < 0.86 ? 'car' : roll < 0.94 ? 'bus' : 'pickup';
      const slot = this.vehicles.pool[type].add();
      if (slot < 0) return;
      const car = { type, road, k, dir, d: 0, speed: 0, x, z, yaw: 0, lean: 0, slot, rider: -1, shirt: -1, pace: 0.85 + this.rand() * 0.3, lane: this.rand() < 0.5 ? 1 : -1, fresh: true, done: false, lit: true, paint: type === 'car' || type === 'pickup' ? paintFor(this.rand()) : null };
      this.vehicles.paint(car);
      if (type === 'bike') {
        car.rider = this.riders.add();
        car.shirt = this.shirts.add();
        this.shirts.tint(car.shirt, this.shirtColours[Math.floor(this.rand() * this.shirtColours.length)]);
      }
      this.cars.push(car);
      return;
    }
  }

  drop(car) {
    this.vehicles.release(car.type, car.slot);
    if (this.riderCards && car.rider >= 0) {
      this.riderCards.setMatrixAt(car.rider, ZERO_MATRIX);
      this.riderCards.instanceMatrix.needsUpdate = true;
    }
    this.riders.remove(car.rider);
    this.shirts.remove(car.shirt);
  }

  // obstacles: [{ x, z }] things to stop for (the player, other players).
  update(dt, px, pz, obstacles) {
    this.clock += dt;
    if (!this.riderCards && this.people && this.people.cards) {
      this.riderCards = this.people.riderCards(this.riders.capacity);
      this.riders.mesh.parent.add(this.riderCards);
      this.riders.mesh.visible = false;
      this.shirts.mesh.visible = false;
    }
    const wanted = Math.round(MAX * this.busy);
    if (this.cars.length < wanted && this.clock > 0.25) {
      this.clock = 0;
      this.spawn(px, pz);
    }
    for (let c = this.cars.length - 1; c >= 0; c--) {
      const car = this.cars[c];
      const far = Math.hypot(car.x - px, car.z - pz);
      // gone with its tile, out of range, or one too many once the streets have emptied
      if (!car.road.live || car.done || far > GONE || (this.cars.length > wanted && far > FAR * 0.6 && c === this.cars.length - 1)) {
        this.drop(car);
        this.cars.splice(c, 1);
        continue;
      }
      this.move(car, dt, obstacles);
    }
  }

  move(car, dt, obstacles) {
    const road = car.road;
    const a = car.k * 2;
    const b = (car.k + car.dir) * 2;
    const ax = road.pts[a];
    const az = road.pts[a + 1];
    const ex = road.pts[b] - ax;
    const ez = road.pts[b + 1] - az;
    const len = Math.hypot(ex, ez) || 0.001;
    const fx = ex / len;
    const fz = ez / len;

    // slow down and stop for whatever is close in front
    let limit = SPEED[road.c] * (road.earth ? 0.7 : 1) * car.pace * (car.type === 'bike' ? 1.08 : 1);
    const ahead = (ox, oz, reach) => {
      const dx = ox - car.x;
      const dz = oz - car.z;
      const along = dx * fx + dz * fz;
      if (along < 0.5 || along > reach) return;
      if (Math.abs(dx * -fz + dz * fx) > 2) return;
      limit = Math.min(limit, Math.max(0, (along - 5) * 1.2));
    };
    for (const s of this.slow) if (Math.abs(s.x - car.x) < 22 && Math.abs(s.z - car.z) < 22) limit = Math.min(limit, 2.5);
    for (const o of obstacles) ahead(o.x, o.z, 16);
    // only what is going the same way: oncoming traffic passes on its own side
    for (const o of this.cars) if (o !== car && Math.cos(o.yaw - car.yaw) > 0.3) ahead(o.x, o.z, 14);

    if (car.speed < limit) car.speed = Math.min(limit, car.speed + 4 * dt);
    else car.speed = Math.max(limit, car.speed - 12 * dt);
    car.d += car.speed * dt;

    if (car.d >= len) {
      // at the next road point: carry on, or take another road if one joins here
      car.d -= len;
      car.k += car.dir;
      const ways = this.exits(road, car.k, car.dir);
      if (ways.length === 0) {
        // a dead end: turn round, unless the road is one-way, where the journey just ends
        if (road.oneWay) car.done = true;
        else car.dir = -car.dir;
      } else {
        // mostly straight on; at the end of a road any way on is as good as another
        const straight = ways[0].road === road;
        const pick = straight && (ways.length === 1 || this.rand() < 0.7) ? ways[0] : ways[(straight ? 1 : 0) + Math.floor(this.rand() * (ways.length - (straight ? 1 : 0)))];
        car.road = pick.road;
        car.k = pick.k;
        car.dir = pick.dir;
      }
      return;
    }

    // keep to the right-hand side of the road
    // (on a one-way road both lanes go the same way, and each vehicle keeps its own)
    const side = road.oneWay ? (car.lane * road.w) / 5 : Math.min(road.w / 4, 2.2);
    const tx = ax + fx * car.d - fz * side;
    const tz = az + fz * car.d + fx * side;
    if (car.fresh) {
      car.x = tx;
      car.z = tz;
      car.yaw = Math.atan2(fx, fz);
      car.fresh = false;
    } else {
      const k = Math.min(1, dt * 6);
      car.x += (tx - car.x) * k;
      car.z += (tz - car.z) * k;
      let diff = Math.atan2(fx, fz) - car.yaw;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      car.yaw += diff * Math.min(1, dt * 5);
    }
    this.vehicles.roll(car, dt);
    this.vehicles.place(car);
    if (car.rider >= 0) {
      // the rider sits where the bike is, tilted with it
      const o = this.tmp;
      o.position.set(car.x, car.y + 0.02, car.z);
      o.rotation.set(0, car.yaw, car.lean || 0);
      o.updateMatrix();
      if (this.riderCards) {
        // the seated card sits on the saddle; the card turns to the camera on its own
        o.position.y = car.y + VEHICLES.bike.seat - 0.08;
        o.rotation.set(0, 0, 0);
        o.updateMatrix();
        this.riderCards.setMatrixAt(car.rider, o.matrix);
        if (car.rider >= this.riderCards.count) this.riderCards.count = car.rider + 1;
        this.riderCards.instanceMatrix.needsUpdate = true;
      } else {
        this.riders.set(car.rider, o.matrix);
        this.shirts.set(car.shirt, o.matrix);
      }
    }
  }

  // Adds the vehicles near a point to a list of things to bump into.
  collect(x, z, list) {
    for (const car of this.cars) {
      if (Math.abs(car.x - x) > 8 || Math.abs(car.z - z) > 8) continue;
      const r = car.type === 'bike' ? 0.5 : (VEHICLES[car.type] || VEHICLES.taxi).radius * 0.9;
      list.push({ p: [car.x - r, car.z - r, car.x + r, car.z - r, car.x + r, car.z + r, car.x - r, car.z + r], minX: car.x - r, minZ: car.z - r, maxX: car.x + r, maxZ: car.z + r, top: car.y + 1.6 });
    }
    return list;
  }
}
